import { createSelector } from '@reduxjs/toolkit';

import type { FreeBet } from 'app/api/services/freebets/types';
import type { ReduxState } from 'app/redux/types';

import {
  AmountValidationResult,
  BetErrorCode,
  StakeErrorCode,
  SubmissionState,
  TypeOfError,
} from '../../constants';
import type { RecordOfEntryLimitsType, RecordOfPaymentOptionsEntry } from '../../records';
import type { ExpressMatchPrices } from '../../types/stakeTypes/express';
import { selectBets, selectMatches } from '../entries';
import { selectExpressStakeTypeState, selectSingleStakeTypeState, selectStakeTypesState } from '../state';
import { selectSubmission } from '../submission';

// ---

export const selectExpressSubmission = (state: ReduxState): SubmissionState => {
  return selectExpressStakeTypeState(state).get('submission');
};

export const selectExpressLimits = (state: ReduxState): RecordOfEntryLimitsType => {
  return selectExpressStakeTypeState(state).get('limits');
};

export const selectExpressMatchPrices = (state: ReduxState): ExpressMatchPrices => {
  return selectExpressStakeTypeState(state).get('matchPrices');
};

export const selectExpressFreeBet = (state: ReduxState): FreeBet | null => {
  return selectExpressStakeTypeState(state).get('freeBet');
};

export const selectExpressPaymentOptions = (state: ReduxState): RecordOfPaymentOptionsEntry => {
  return selectExpressStakeTypeState(state).get('paymentOptions');
};

export type SelectExpressPricesValue = {
  prevPrice: number;
  nextPrice: number;
};

export const selectExpressPrices = createSelector(
  selectExpressMatchPrices,
  selectBets,
  selectMatches,
  (prices, bets, matches) => {
    let prevPrice = 1;
    let nextPrice = 1;

    const matchIdsWithOneBet = new Set();

    prices.forEach((price, matchId) => { // Нужно для экспрессов внутри матча
      const match = matches.get(matchId);

      if (!match) {
        console.error('selectExpressPrices -> `match` not found');
        return;
      }

      const isExpressInsideMatch = match.betIds.size > 1;

      if (!isExpressInsideMatch) {
        matchIdsWithOneBet.add(matchId);
        return;
      }

      if (price.entityPrice) {
        nextPrice *= price.entityPrice;
      }
      else {
        nextPrice *= price.entryPrice;
      }

      prevPrice *= price.entryPrice;
    });

    bets.forEach((bet) => {
      if (matchIdsWithOneBet.has(bet.entity.matchId)) {
        prevPrice *= bet.price;
        nextPrice *= bet.entity.price || 0;
      }
    });

    return {
      prevPrice,
      nextPrice,
    };
  },
);

export const selectExpressMaxWinning = (state: ReduxState): number => {
  const prices = selectExpressPrices(state);
  const disabled = selectSubmission(state) !== SubmissionState.AWAITING;
  const nextPrice = disabled ? prices.prevPrice : prices.nextPrice;
  if (typeof nextPrice !== 'number') {
    return 0;
  }

  const amount = selectStakeTypesState(state).amount;

  if (Number.isNaN(amount.numeric)) {
    return 0;
  }

  return nextPrice * amount.numeric;
};

export const selectExpressFreebetMaxWinning = (state: ReduxState): number => {
  const prices = selectExpressPrices(state);
  const disabled = selectSubmission(state) !== SubmissionState.AWAITING;
  const nextPrice = disabled ? prices.prevPrice : prices.nextPrice;
  if (typeof nextPrice !== 'number') {
    return 0;
  }

  const freebetAmount = selectExpressFreeBet(state)?.amount;

  if (!freebetAmount || Number.isNaN(freebetAmount)) {
    return 0;
  }


  return (nextPrice * freebetAmount) - freebetAmount;
};

export const selectExpressAmountValidation = (state: ReduxState): AmountValidationResult => {
  const amount = selectStakeTypesState(state).amount;

  if (Number.isNaN(amount.numeric)) {
    return AmountValidationResult.EMPTY_VALUE;
  }

  const limits = selectExpressLimits(state);

  if (limits === null) {
    return AmountValidationResult.NO_ERRORS;
  }

  if (amount.numeric < limits.min) {
    return AmountValidationResult.MIN_NOT_REACHED;
  }

  if (amount.numeric > limits.max) {
    return AmountValidationResult.BIG_STAKE;
  }

  return AmountValidationResult.NO_ERRORS;
};

export const selectExpressInsideMatchError = createSelector(
  (state: ReduxState) => selectSingleStakeTypeState(state).betsInfo,
  (betsInfo) => {
    let entryError = null;

    for (const entry of betsInfo.values()) {
      const error = entry?.get('error');

      if (!error) {
        continue;
      }

      if (
        error.code === BetErrorCode.PRICE_CHANGED
        || error.code === StakeErrorCode.INTERNAL_SYSTEM_ERROR
        || error.code === StakeErrorCode.NOT_ENOUGH_MONEY
      ) {
        continue;
      }

      if (error.type === TypeOfError.MATCH || error.type === TypeOfError.GENERAL) {
        return error;
      }
      else if (error.type === TypeOfError.BET) {
        entryError = error;
      }
    }

    return entryError;
  },
);
